/**
 * Profit Analyzer Import Service
 * Imports expense data from Amazon Analyzer's Profitability module
 */

import type {
  PricingCalculatorImport,
  PricingCalculatorImportV1,
  PricingCalculatorImportV2,
  PricingCategoryExpense,
  PricingCategoryExpenseV1,
  CategoryColumnDefaults,
} from '../types/database';
import {
  upsertCategoryColumnDefaults,
  getCategoryDefaultsByMarketplace,
  getAllCustomMarketplaces,
  clearMarketplaceCategoryDefaults,
} from '../utils/database';
import { logger } from '../utils/logger';

/**
 * Get Amazon marketplace ID dynamically
 * The ID is generated when marketplace is created, so we need to look it up
 */
const getAmazonMarketplaceId = async (): Promise<string> => {
  const marketplaces = await getAllCustomMarketplaces();
  const amazon = marketplaces.find(m => m.shortName === 'AMZ');
  if (!amazon) {
    throw new Error('Amazon marketplace not found. Please initialize marketplaces first.');
  }
  return amazon.id;
};

/**
 * Type guard to check if import data is v2 format
 */
export const isV2Import = (data: PricingCalculatorImport): data is PricingCalculatorImportV2 => {
  return data.version === 2;
};

/**
 * Type guard to check if category expense is v1 format
 */
export const isV1Category = (category: PricingCategoryExpense): category is PricingCategoryExpenseV1 => {
  return 'sampleSize' in category && 'totalRevenue' in category;
};

/**
 * Fetch marketplace profit config from backend
 * Used to fill missing fields in v2 format
 * TODO: Implement backend endpoint /api/marketplaces/profit-configs/:code
 */
const getMarketplaceProfitConfig = async (marketplaceCode: string): Promise<{
  fba_config: {
    shippingPerDesi: number;
    warehousePercent: number;
  };
  fbm_config: {
    fromTR: {
      ddpFee: number;
      customsDutyPercent: number;
      categoryDuties?: Array<{ category: string; dutyPercent: number }>;
    };
  };
  gst_config: {
    gstPercent: number;
  };
}> => {
  // TODO: Backend endpoint not implemented yet - using default values
  logger.debug(`Using default profit config for ${marketplaceCode}`);
  return {
    fba_config: { shippingPerDesi: 0, warehousePercent: 0 },
    fbm_config: { fromTR: { ddpFee: 2.5, customsDutyPercent: 8.5 } },
    gst_config: { gstPercent: 0 },
  };
};

/**
 * Map marketplace code to country code
 * Profit Analyzer uses US, UK etc. which matches our country codes
 */
const normalizeMarketplaceCode = (code: string): string => {
  return code.toUpperCase();
};

/**
 * Map fulfillment type from Profit Analyzer to Pricing Calculator
 * v2 format already has FBM-TR/FBM-US distinction, v1 needs mapping
 */
const mapFulfillmentType = (
  fulfillmentType: 'FBA' | 'FBM' | 'FBM-TR' | 'FBM-US' | 'Mixed',
  marketplace: string
): string => {
  // v2 format: FBM-TR and FBM-US are already explicit
  if (fulfillmentType === 'FBM-TR' || fulfillmentType === 'FBM-US') {
    return fulfillmentType;
  }

  // v1/v2 format: FBA is always FBA
  if (fulfillmentType === 'FBA') return 'FBA';

  // v1 format: Mixed defaults to FBA
  if (fulfillmentType === 'Mixed') return 'FBA';

  // v1 format: FBM - US defaults to FBM-TR (Turkey source)
  if (marketplace === 'US') {
    return 'FBM-TR';
  }

  return 'FBM';
};

/**
 * Convert Profit Analyzer category data to CategoryColumnDefaults
 * Supports both v1 (legacy) and v2 (new) formats
 */
const convertToCategoryDefaults = async (
  category: PricingCategoryExpense,
  importData: PricingCalculatorImport,
  amazonMarketplaceId: string,
  marketplaceProfitConfig?: {
    fba_config: { shippingPerDesi: number; warehousePercent: number };
    fbm_config: { fromTR: { ddpFee: number; customsDutyPercent: number } };
    gst_config: { gstPercent: number };
  }
): Promise<Omit<CategoryColumnDefaults, 'id' | 'createdAt' | 'updatedAt'>> => {
  const countryCode = normalizeMarketplaceCode(category.marketplace);
  const fulfillmentType = mapFulfillmentType(category.fulfillmentType, countryCode);

  // Detect format version
  const isV1 = isV1Category(category);
  const isV2 = !isV1;

  // For v2, get global settings
  let advertisingPercent = 0;
  let fbaCostPercent = 0;
  let fbmCostPercent = 0;

  if (isV2 && isV2Import(importData)) {
    advertisingPercent = importData.globalSettings.advertisingPercent;
    fbaCostPercent = importData.globalSettings.fbaCostPercent;
    fbmCostPercent = importData.globalSettings.fbmCostPercent;
  } else if (isV1) {
    // v1 format has these in each category
    const v1Cat = category as PricingCategoryExpenseV1;
    advertisingPercent = v1Cat.advertisingPercent;
    fbaCostPercent = v1Cat.fbaCostPercent;
    fbmCostPercent = v1Cat.fbmCostPercent;
  }

  // Map expense percentages to column keys
  const columnDefaults: Record<string, number | string> = {
    // Platform fees (from transactions - both v1 and v2)
    sellingPercent: category.sellingFeePercent,
    fbaFeePercent: category.fbaFeePercent,
    refundPercent: category.refundLossPercent,
    vatPercent: category.vatPercent,

    // General costs (from global or category)
    adsPercent: advertisingPercent,
    fbaCostPercent: fbaCostPercent,
    fbmCostPercent: fbmCostPercent,

    // Product cost (both v1 and v2)
    // Note: productCostPercent is not directly used in column defaults
    // It's only for reference in Profit Analyzer

    // GST (both v1 and v2)
    gstPercent: category.gstCostPercent,
  };

  // For v1: Use values from export
  // For v2: Fill from PriceLab's marketplace_profit_configs
  if (isV1) {
    const v1Cat = category as PricingCategoryExpenseV1;
    columnDefaults.shipPercent = v1Cat.shippingCostPercent;
    columnDefaults.customsPercent = v1Cat.customsDutyPercent;
    columnDefaults.ddpPercent = v1Cat.ddpFeePercent;
    columnDefaults.warehousePercent = v1Cat.warehouseCostPercent;
  } else if (isV2 && marketplaceProfitConfig) {
    // v2: Fill from PriceLab settings
    // Note: shippingCostPercent is calculated from desi + shipping_rate_brackets at pricing time
    // So we set it to 0 here (not used in column defaults)
    columnDefaults.shipPercent = 0;

    // Customs duty from config
    columnDefaults.customsPercent = marketplaceProfitConfig.fbm_config.fromTR.customsDutyPercent;

    // DDP fee - convert from fixed amount to percentage (approximate)
    // Note: This is rough - actual DDP should be calculated per-order
    columnDefaults.ddpPercent = 0; // Cannot convert fixed amount to % without price

    // Warehouse cost from config
    columnDefaults.warehousePercent = marketplaceProfitConfig.fba_config.warehousePercent;
  }

  // Target margin and sample size (v1 only)
  const targetMargin = isV1 ? (category as PricingCategoryExpenseV1).avgProfitMargin : 15; // Default 15% for v2
  const sampleSize = isV1 ? (category as PricingCategoryExpenseV1).sampleSize : 0; // Unknown for v2

  return {
    marketplaceId: amazonMarketplaceId,
    countryCode,
    category: category.category,
    fulfillmentType,
    targetMargin,
    columnDefaults,
    sampleSize,
    dataSource: 'imported',
    periodStart: new Date(category.periodStart),
    periodEnd: new Date(category.periodEnd),
  };
};

/**
 * Import category expense data from Profit Analyzer export file
 */
export const importFromProfitAnalyzer = async (
  file: File
): Promise<{
  imported: number;
  updated: number;
  skipped: number;
  marketplace: string;
  categories: string[];
}> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const jsonString = e.target?.result as string;
        const importData = JSON.parse(jsonString) as PricingCalculatorImport;

        // Validate import data
        if (!importData.version || importData.sourceApp !== 'amazon-analyzer') {
          throw new Error('Invalid import file. Expected export from Amazon Analyzer.');
        }

        if (!importData.categories || importData.categories.length === 0) {
          throw new Error('No category data found in import file.');
        }

        // Get Amazon marketplace ID dynamically
        const amazonMarketplaceId = await getAmazonMarketplaceId();

        // Detect import version and get marketplace code(s)
        const isV2 = isV2Import(importData);
        let marketplaceCode: string;

        if (isV2) {
          // v2: Get marketplace from first category (all same marketplace in single export)
          marketplaceCode = importData.categories[0]?.marketplace || 'US';
        } else {
          // v1: Has marketplace at root level
          marketplaceCode = (importData as PricingCalculatorImportV1).marketplace;
        }

        const result = {
          imported: 0,
          updated: 0,
          skipped: 0,
          marketplace: marketplaceCode,
          categories: [] as string[],
        };

        // For v2, fetch marketplace profit config to fill missing fields
        let marketplaceProfitConfig;
        if (isV2) {
          logger.debug(`🔍 v2 import detected - fetching profit config for ${marketplaceCode}`);
          marketplaceProfitConfig = await getMarketplaceProfitConfig(marketplaceCode);
        }

        // Get existing defaults to check for updates
        const existingDefaults = await getCategoryDefaultsByMarketplace(
          amazonMarketplaceId,
          marketplaceCode.toUpperCase()
        );

        logger.debug('🔍 Import starting for marketplace:', amazonMarketplaceId);
        logger.debug('🔍 Import version:', importData.version);
        logger.debug('🔍 Categories to import:', importData.categories.length);

        // Process each category
        for (const category of importData.categories) {
          try {
            const categoryDefaults = await convertToCategoryDefaults(
              category,
              importData,
              amazonMarketplaceId,
              marketplaceProfitConfig
            );
            logger.debug('🔍 Processing category:', category.category, 'fulfillment:', categoryDefaults.fulfillmentType);

            // Check if defaults already exist for this category/marketplace/fulfillment combo
            const existing = existingDefaults.find(d =>
              d.category === category.category &&
              d.fulfillmentType === categoryDefaults.fulfillmentType
            );

            // upsertCategoryColumnDefaults handles both insert and update
            await upsertCategoryColumnDefaults(categoryDefaults);
            logger.debug('✅ Saved category:', category.category);

            if (existing) {
              result.updated++;
            } else {
              result.imported++;
            }

            result.categories.push(category.category);
          } catch (catError) {
            logger.error(`❌ Failed to import category ${category.category}:`, catError);
            result.skipped++;
          }
        }

        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

/**
 * Get imported category defaults for a marketplace
 */
export const getImportedCategoryDefaults = async (
  countryCode: string,
  fulfillmentType?: string
): Promise<CategoryColumnDefaults[]> => {
  const amazonMarketplaceId = await getAmazonMarketplaceId();
  const defaults = await getCategoryDefaultsByMarketplace(amazonMarketplaceId, countryCode);

  // Filter by fulfillment type if specified
  if (fulfillmentType) {
    return defaults.filter(d => d.fulfillmentType === fulfillmentType);
  }

  // Filter only imported data
  return defaults.filter(d => d.dataSource === 'imported');
};

/**
 * Parse and preview import data without saving
 */
export const previewProfitAnalyzerImport = async (
  file: File
): Promise<PricingCalculatorImport> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const jsonString = e.target?.result as string;
        const importData = JSON.parse(jsonString) as PricingCalculatorImport;

        if (!importData.version || importData.sourceApp !== 'amazon-analyzer') {
          throw new Error('Invalid import file. Expected export from Amazon Analyzer.');
        }

        resolve(importData);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

// ============================================
// BULK IMPORT (Multi-marketplace)
// ============================================

/**
 * Bulk import data structure (from Amazon Analyzer's bulk export)
 */
export interface BulkPricingImport {
  version: number;
  exportedAt: string;
  sourceApp: 'amazon-analyzer';
  dateRange: {
    start: string;
    end: string;
  };
  marketplaces: {
    [key: string]: PricingCalculatorImport;
  };
  summary: {
    totalMarketplaces: number;
    totalCategories: number;
    totalRevenue?: number;  // V1 only (removed in V2)
  };
}

/**
 * Import result for bulk import
 */
export interface BulkImportResult {
  totalImported: number;
  totalUpdated: number;
  totalSkipped: number;
  byMarketplace: {
    [key: string]: {
      imported: number;
      updated: number;
      skipped: number;
      categories: string[];
    };
  };
}

/**
 * Import bulk data from Amazon Analyzer (all marketplaces at once)
 */
export const importBulkFromProfitAnalyzer = async (
  file: File
): Promise<BulkImportResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const jsonString = e.target?.result as string;
        const bulkData = JSON.parse(jsonString) as BulkPricingImport;

        // Validate bulk import data
        if (!bulkData.version || bulkData.sourceApp !== 'amazon-analyzer') {
          throw new Error('Invalid import file. Expected bulk export from Amazon Analyzer.');
        }

        if (!bulkData.marketplaces || Object.keys(bulkData.marketplaces).length === 0) {
          throw new Error('No marketplace data found in import file.');
        }

        // Get Amazon marketplace ID dynamically
        const amazonMarketplaceId = await getAmazonMarketplaceId();

        // 🧹 AUTOMATIC CLEANUP: Clear all existing Amazon category defaults before import
        // This prevents data pollution from previous imports
        logger.debug('🧹 Clearing existing Amazon marketplace defaults...');
        try {
          const cleared = await clearMarketplaceCategoryDefaults(amazonMarketplaceId);
          logger.debug(`✅ Cleared ${cleared} existing defaults - starting fresh import`);
        } catch (clearError) {
          logger.error('⚠️  Failed to clear existing defaults:', clearError);
          // Continue with import anyway - upsert will update existing entries
        }

        const result: BulkImportResult = {
          totalImported: 0,
          totalUpdated: 0,
          totalSkipped: 0,
          byMarketplace: {},
        };

        // Process each marketplace
        for (const [marketplaceCode, marketplaceData] of Object.entries(bulkData.marketplaces)) {
          const mpResult = {
            imported: 0,
            updated: 0,
            skipped: 0,
            categories: [] as string[],
          };

          // For v2, fetch marketplace profit config
          const isV2 = isV2Import(marketplaceData);
          let marketplaceProfitConfig;
          if (isV2) {
            logger.debug(`🔍 v2 bulk import - fetching profit config for ${marketplaceCode}`);
            marketplaceProfitConfig = await getMarketplaceProfitConfig(marketplaceCode);
          }

          // Get existing defaults for this marketplace
          const existingDefaults = await getCategoryDefaultsByMarketplace(
            amazonMarketplaceId,
            marketplaceCode.toUpperCase()
          );

          // Process each category in this marketplace
          for (const category of marketplaceData.categories) {
            try {
              const categoryDefaults = await convertToCategoryDefaults(
                category,
                marketplaceData,
                amazonMarketplaceId,
                marketplaceProfitConfig
              );

              // Check if defaults already exist
              const existing = existingDefaults.find(d =>
                d.category === category.category &&
                d.fulfillmentType === categoryDefaults.fulfillmentType
              );

              await upsertCategoryColumnDefaults(categoryDefaults);

              if (existing) {
                mpResult.updated++;
              } else {
                mpResult.imported++;
              }

              if (!mpResult.categories.includes(category.category)) {
                mpResult.categories.push(category.category);
              }
            } catch (catError) {
              logger.error(`❌ Failed to import category ${category.category} for ${marketplaceCode}:`, catError);
              mpResult.skipped++;
            }
          }

          result.byMarketplace[marketplaceCode] = mpResult;
          result.totalImported += mpResult.imported;
          result.totalUpdated += mpResult.updated;
          result.totalSkipped += mpResult.skipped;
        }

        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

/**
 * Preview bulk import data without saving
 */
export const previewBulkImport = async (
  file: File
): Promise<BulkPricingImport> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const jsonString = e.target?.result as string;
        const bulkData = JSON.parse(jsonString) as BulkPricingImport;

        if (!bulkData.version || bulkData.sourceApp !== 'amazon-analyzer') {
          throw new Error('Invalid import file. Expected bulk export from Amazon Analyzer.');
        }

        resolve(bulkData);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

// ============================================
// COPY FROM ANOTHER COUNTRY
// ============================================

/**
 * Copy category defaults from one country to another
 * Useful for new markets without sales data (e.g., copy UK data for SA)
 */
export const copyCountryDefaults = async (
  sourceCountryCode: string,
  targetCountryCode: string,
  options?: {
    fulfillmentType?: 'FBA' | 'FBM' | 'both';
    categories?: string[]; // If not specified, copy all
    overwriteExisting?: boolean;
  }
): Promise<{
  copied: number;
  skipped: number;
  categories: string[];
}> => {
  const amazonMarketplaceId = await getAmazonMarketplaceId();

  // Get source country defaults
  const sourceDefaults = await getCategoryDefaultsByMarketplace(
    amazonMarketplaceId,
    sourceCountryCode.toUpperCase()
  );

  if (sourceDefaults.length === 0) {
    throw new Error(`No category defaults found for source country: ${sourceCountryCode}`);
  }

  // Get existing target country defaults (to check for overwrites)
  const targetDefaults = await getCategoryDefaultsByMarketplace(
    amazonMarketplaceId,
    targetCountryCode.toUpperCase()
  );

  const result = {
    copied: 0,
    skipped: 0,
    categories: [] as string[],
  };

  // Filter source defaults by options
  let filteredDefaults = sourceDefaults;

  if (options?.fulfillmentType && options.fulfillmentType !== 'both') {
    const ftMap: Record<string, string[]> = {
      'FBA': ['FBA'],
      'FBM': ['FBM', 'FBM-TR', 'FBM-US'],
    };
    const allowedTypes = ftMap[options.fulfillmentType] || [options.fulfillmentType];
    filteredDefaults = filteredDefaults.filter(d => allowedTypes.includes(d.fulfillmentType));
  }

  if (options?.categories && options.categories.length > 0) {
    filteredDefaults = filteredDefaults.filter(d => options.categories!.includes(d.category));
  }

  // Copy each default to target country
  for (const sourceDefault of filteredDefaults) {
    // Check if target already has this category/fulfillment combo
    const existingTarget = targetDefaults.find(d =>
      d.category === sourceDefault.category &&
      d.fulfillmentType === sourceDefault.fulfillmentType
    );

    if (existingTarget && !options?.overwriteExisting) {
      result.skipped++;
      continue;
    }

    // Create new default for target country
    const targetDefault: Omit<CategoryColumnDefaults, 'id' | 'createdAt' | 'updatedAt'> = {
      marketplaceId: amazonMarketplaceId,
      countryCode: targetCountryCode.toUpperCase(),
      category: sourceDefault.category,
      fulfillmentType: sourceDefault.fulfillmentType,
      targetMargin: sourceDefault.targetMargin,
      columnDefaults: { ...sourceDefault.columnDefaults },
      sampleSize: sourceDefault.sampleSize,
      dataSource: 'imported', // Mark as imported (copied)
      periodStart: sourceDefault.periodStart,
      periodEnd: sourceDefault.periodEnd,
    };

    await upsertCategoryColumnDefaults(targetDefault);
    result.copied++;

    if (!result.categories.includes(sourceDefault.category)) {
      result.categories.push(sourceDefault.category);
    }
  }

  return result;
};

/**
 * Get available countries with category defaults
 * Useful for showing which countries can be used as source
 */
export const getCountriesWithDefaults = async (): Promise<{
  countryCode: string;
  categoryCount: number;
  fulfillmentTypes: string[];
}[]> => {
  const amazonMarketplaceId = await getAmazonMarketplaceId();
  const marketplaces = await getAllCustomMarketplaces();
  const amazon = marketplaces.find(m => m.id === amazonMarketplaceId);

  if (!amazon) return [];

  const result: {
    countryCode: string;
    categoryCount: number;
    fulfillmentTypes: string[];
  }[] = [];

  for (const country of amazon.countries) {
    const defaults = await getCategoryDefaultsByMarketplace(amazonMarketplaceId, country.code);

    if (defaults.length > 0) {
      const categories = new Set(defaults.map(d => d.category));
      const fulfillmentTypes = Array.from(new Set(defaults.map(d => d.fulfillmentType)));

      result.push({
        countryCode: country.code,
        categoryCount: categories.size,
        fulfillmentTypes,
      });
    }
  }

  return result;
};
